import { MonthInput } from '../../../types';
import { monthAliasOverrides } from '../../../constants/dateConstants';
import { LOCALES, LocaleType } from '../../../constants/localeConstants';

type MonthNameVariants = {
  shortMap: Record<string, number>;
  longMap: Record<string, number>;
};

const monthNameCache: Partial<Record<LocaleType, MonthNameVariants>> = {};

function buildMonthNames(locale: string): MonthNameVariants {
  const formatterShort = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  const formatterLong = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  // Initialize maps to store month names and zero-based month indices
  const shortMap: Record<string, number> = {};
  const longMap: Record<string, number> = {};

  const monthDates = Array.from({ length: 12 }, (_, i) => new Date(Date.UTC(2020, i, 1)));

  monthDates.forEach((date, monthIndex) => {
    const shortName = formatterShort.format(date);
    const longName = formatterLong.format(date);
    shortMap[shortName.toLowerCase()] = monthIndex;
    longMap[longName.toLowerCase()] = monthIndex;
  });

  return { shortMap, longMap };
}

export function getMonthNames(locale: LocaleType = LOCALES.EN_US): MonthNameVariants {
  if (!monthNameCache[locale]) {
    monthNameCache[locale] = buildMonthNames(locale);
  }

  return monthNameCache[locale]!;
}

export function resolveMonthName(name: string, locale: LocaleType): number | undefined {
  const { shortMap, longMap } = getMonthNames(locale);
  const normalized = name.toLowerCase();

  return shortMap[normalized] ?? longMap[normalized] ?? monthAliasOverrides[locale][normalized] ?? undefined;
}

/**
 * Returns 0-based month index from a numeric string ("01" → 0).
 */
export const getMonthIndexFromNumeric = (value: string): number | undefined => {
  const num = Number(value);
  if (num < 1 || num > 12) {
    // Invalid month range (1-12).
    return undefined;
  }
  return num - 1; // 0-based index
};

export const getMonthIndex = ({ numericMonth, month, locale }: MonthInput): number | undefined => {
  const definedInputs = [!!numericMonth, !!month].filter(Boolean).length;
  if (definedInputs > 1) {
    console.error(
      `[getMonthIndex] Only one of numericMonth, month should be provided. Passed: ${JSON.stringify({
        numericMonth,
        month,
      })}`,
    );
    return undefined;
  }
  if (numericMonth) {
    return getMonthIndexFromNumeric(numericMonth);
  }
  if (month) {
    const zeroBasedMonthIndex = resolveMonthName(month, locale);
    if (typeof zeroBasedMonthIndex === 'undefined') {
      return undefined; // Invalid month name:
    }
    return zeroBasedMonthIndex;
  }
  return undefined;
};
